import { useState } from 'react';
import type { FC } from 'react';
import Layout from '../../components/Layout';
import ConfirmDialog from '../../components/ConfirmDialog';
import VocabEntryCard from './VocabEntryCard';
import { useTranslation } from '../../i18n/I18nContext';
import { useToast } from '../../contexts/ToastContext';
import { IconButton } from '../../components/ui/icon-button';
import { Dialog } from '../../components/ui/dialog';
import { FloatingPanel } from '../../components/ui/floating-panel';
import { useVocab } from '../articles/VocabContext';
import { exportVocabAsText, type ExportType } from '../articles/vocab-store';

const VocabList: FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { entries, deleteEntry, clearAll } = useVocab();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('raw');

  const handleDelete = async (articleId: string, vocabId: string) => {
    try {
      await deleteEntry(articleId, vocabId);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('vocab.delete_failed'), 'error');
    }
  };

  const handleClearAll = async () => {
    try {
      await clearAll();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('vocab.clear_failed'), 'error');
    } finally {
      setShowClearConfirm(false);
    }
  };

  const handleExport = () => {
    const text = exportVocabAsText(entries, exportType);
    if (!text) {
      showToast(t('vocab.no_vocab'), 'warning');
      return;
    }
    setShowExportDialog(false);
    showToast(t('vocab.copied'), 'success');
    navigator.clipboard.writeText(text).catch(() => {
      showToast(t('vocab.copy_failed'), 'error');
    });
  };

  return (
    <Layout title="">
      {entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="text-4xl mb-3">{'📚'}</div>
          <p className="text-lg mb-2">{t('vocab.no_vocab')}</p>
          <p className="text-sm mb-4">{t('vocab.no_vocab_description')}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 pr-[50px]">
            {entries.map((entry) => (
              <VocabEntryCard
                key={entry.vocabId}
                entry={entry}
                onDelete={() => handleDelete(entry.articleId, entry.vocabId)}
              />
            ))}
          </div>

          {/* Floating export + clear buttons — right edge, vertically centered */}
          <FloatingPanel side="end">
            <IconButton onClick={() => setShowExportDialog(true)} title={t('vocab.export_quizlet')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="14" height="14" x="8" y="8" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
            </IconButton>
            <IconButton onClick={() => setShowClearConfirm(true)} title={t('vocab.clear_all')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </IconButton>
          </FloatingPanel>
        </>
      )}

      <ConfirmDialog
        open={showClearConfirm}
        title={t('vocab.clear_all_title')}
        message={t('vocab.clear_all_message', { count: entries.length })}
        confirmLabel={t('vocab.clear_all')}
        destructive
        onConfirm={handleClearAll}
        onCancel={() => setShowClearConfirm(false)}
      />

      <Dialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        title={t('vocab.export_title')}
        maxWidth="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setShowExportDialog(false)}
              className="px-4 py-2 rounded-md text-sm border border-border text-foreground hover:bg-muted transition-colors"
            >
              {t('vocab.cancel')}
            </button>
            <button
              onClick={handleExport}
              className="px-5 py-2 rounded-md font-semibold text-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              {t('vocab.export_quizlet')}
            </button>
          </div>
        }
      >
        <div className="px-4 sm:px-6 py-4">
          <select
            value={exportType}
            onChange={(e) => setExportType(e.target.value as ExportType)}
            className="w-full border border-input rounded-md px-3 py-2 bg-background text-foreground text-sm"
          >
            <option value="raw">{t('vocab.export_raw')}</option>
            <option value="normalized">{t('vocab.export_normalized')}</option>
          </select>
        </div>
      </Dialog>
    </Layout>
  );
};

export default VocabList;
